import React, { useCallback, useState } from 'react';
import ProfileImage from '../../atoms/ProfileImage';
import MentionAddComment from '../../molecules/MentionComment/MentionAddComment';
import MentionModifyComment from '../../molecules/MentionComment/MentionModifyComment';
import ProfilePopup from '../../organisms/Popup/ProfilePopup';
import ShortsMediaEmojiComment from './ShortsMediaEmojiComment';
import { CommentPostType } from '../../../types/enum/CommentPostType';
import { getCommentContentsByReplacedMentionTagToHTMLTag } from '../../../utils/mentionUtils';
import { getDangerouslySetInnerHTMLValueBySanitizedHtml } from '../../../utils/LGLifeUtils';
import { useMyInfo } from '../../../hooks';
import { relativeTime } from '../../../utils/utils';
import moment from 'moment/moment';
import classNames from 'classnames';

export interface ShortsCommentData extends CommentResDto {
  id: string;
  type: 'fixed' | 'best' | 'normal';
  content: string;
  likes: number;
  isLiked?: boolean;
  isMine?: boolean;
  isModified?: boolean;
  bestRank?: number;
  replyCount?: number;
  delUser?: 'SELF' | 'ADMIN';
}

export interface ShortsReplyData extends CommentResDto {
  id: string;
  author: {
    name: string;
    company?: string;
    isAdmin: boolean;
    profileImage?: string;
  };
  content: string;
  likes: number;
  isLiked?: boolean;
  isMine?: boolean;
  isModified?: boolean;
}

export interface ShortsCommentItemProps {
  comment: ShortsCommentData;
  contentsId: string;
  onLike?: (commentId: string) => void;
  onReply?: (commentId: string) => void;
  onUpdate?: () => void;
  commentSearchMailWidth?: () => void;
  // 답글 관련 props 추가
  isReplyInputOpen?: boolean;
  isReplyOpen?: boolean;
  replyList?: CommentResDto[];
  onReplyLike?: (replyId: string, commentId: string) => void;
  onShowReplies?: (commentId: string) => void;
  onSetReplyList?: (commentId: string, replyList: CommentResDto[]) => void;
  // 수정/삭제 관련 props 추가
  onModify?: (commentId: string, newContent: string) => void;
  onDelete?: (commentId: string) => void;
  onReplyModify?: (replyId: string, commentId: string, newContent: string) => void;
  onReplyDelete?: (replyId: string, commentId: string) => void;
}

/**
 * ShortsMedia용 댓글 아이템 컴포넌트
 */
const ShortsCommentItem: React.FC<ShortsCommentItemProps> = ({ 
  comment, 
  contentsId,
  onLike, 
  onReply,
  onUpdate,
  commentSearchMailWidth = () => {},
  // 답글 관련 props
  isReplyInputOpen = false,
  isReplyOpen = false,
  replyList = [],
  onReplyLike,
  onShowReplies,
  onSetReplyList,
  // 수정/삭제 관련 props
  onModify,
  onDelete,
  onReplyModify,
  onReplyDelete
}) => {


  const myInfo = useMyInfo();

    
  // 답글 ref들
  const replyDivRef = React.useRef<HTMLDivElement>(null);
  const replyInputRef = React.useRef<HTMLDivElement>(null);
  
  // 수정/삭제 관련 상태
  const [isModifying, setIsModifying] = React.useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = React.useState(false);
  
  // 멘션 관련 상태
  const [mentionProfilePopup, setMentionProfilePopup] = React.useState(false);
  const [mentionUserId, setMentionUserId] = React.useState('');
  const [parsedComment, setParsedComment] = React.useState('');
  const [parsedReplies, setParsedReplies] = React.useState<{[key: string]: string}>({});
  
  // 메뉴 관련 ref 추가
  const cmtItemWrapRef = React.useRef<HTMLDivElement>(null);
  
  // 수정 관련 ref들
  const modifyCmtDivRef = React.useRef<HTMLDivElement>(null);
  const modifyCmtInputRef = React.useRef<HTMLDivElement>(null);
  
  // 답글 메뉴 상태 관리
  const [activeReplyMenus, setActiveReplyMenus] = React.useState<{[replyId: string]: boolean}>({});
  
  // 답글 수정 상태 관리
  const [replyModifyStates, setReplyModifyStates] = React.useState<{[replyId: string]: boolean}>({});
  
  // 답글 삭제 확인 팝업 상태 관리
  const [replyDeleteConfirm, setReplyDeleteConfirm] = React.useState<{[replyId: string]: boolean}>({});

  const [showProfilePopup, setShowProfilePopup] = useState<boolean>(false);
  const [profilePopupCommentCreatorId, setProfilePopupCommentCreatorId] = useState<string>('');
  
  // 댓글 내용 멘션 파싱
  React.useEffect(() => {
    if (!comment.content) return;
    setParsedComment(getCommentContentsByReplacedMentionTagToHTMLTag(comment.content));
  }, [comment.content]);

  // 답글 내용 멘션 파싱
  React.useEffect(() => {
    if (!replyList || replyList.length === 0) return;
    const newParsedReplies: {[key: string]: string} = {};
    replyList.forEach(reply => {
      if (reply.contents) {
        newParsedReplies[reply.commentId] = getCommentContentsByReplacedMentionTagToHTMLTag(reply.contents);
      }
    });
    setParsedReplies(newParsedReplies);
  }, [replyList]);
  
  // 멘션 클릭 핸들러 추가
  const handleMentionClick = React.useCallback((element: HTMLElement) => {
    element.querySelectorAll('.mention_tag').forEach(tag => {
      tag.addEventListener('click', () => {
        setMentionUserId(tag.getAttribute('data-mention-user-id') as string);
        setMentionProfilePopup(true);
      });
    });
  }, []);
  
  const handleLike = () => {
    if (onLike) {
      onLike(comment.id);
    }
  };

  const handleReply = () => {
    if (onReply) {
      onReply(comment.id);
    }
  };
  
  const handleShowReplies = async () => {
    
    if (onShowReplies) {
      onShowReplies(comment.id);
    }
  };
  
  const handleReplyUpdate = () => {
    // 부모 컴포넌트의 업데이트 함수 호출
    if (onUpdate) onUpdate();
  };
  
  const handleReplyLike = async (replyId: string) => {
    try {
      if (onReplyLike) {
        onReplyLike(replyId, comment.id);
      }
    } catch (error) {
      // 답글 좋아요 실패 처리
    }
  };

  /**
   * 댓글 작성 일시 또는 수정 일시 가져오기 (관리자 전용)
   */
  const getCommentTime = useCallback((isModified: boolean) => {
    if (myInfo.info?.adminYn !== 'Y') return '';
    if (isModified) {
      return relativeTime(comment.modifiedDt, moment().format('YYYY.MM.DD HH:mm:ss'), {showFullDateTime: true});
    }
    return relativeTime(comment.createdDt, moment().format('YYYY.MM.DD HH:mm:ss'), {showFullDateTime: true})
  },[comment, myInfo.info?.adminYn]);
  
  // 수정/삭제 핸들러들
  const handleModify = () => {
    cmtItemWrapRef.current?.classList.remove('is-active');
    cmtItemWrapRef.current?.classList.add('is-modify');
    setIsModifying(true);
  };
  
  // MentionModifyComment의 is-modify 클래스 변화를 감지하여 상태 동기화
  React.useEffect(() => {
    const observer = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        const target = mutation.target as HTMLElement;
        
        // 댓글 수정 상태 동기화
        if (target === cmtItemWrapRef.current) {
          const hasModifyClass = target.classList.contains('is-modify');
          setIsModifying(hasModifyClass);
        }
        
        // 답글 수정 상태 동기화
        const replyId = target.getAttribute('data-reply-id');
        if (replyId && target.classList.contains('comment_item_wrap')) {
          const hasModifyClass = target.classList.contains('is-modify');
          setReplyModifyStates(prev => ({
            ...prev,
            [replyId]: hasModifyClass
          }));
        }
      });
    });
    
    // 댓글 요소 관찰
    if (cmtItemWrapRef.current) {
      observer.observe(cmtItemWrapRef.current, {
        attributes: true,
        attributeFilter: ['class']
      });
    }
    
    // 답글 요소들도 관찰 (답글이 렌더링될 때마다 새로 관찰)
    const replyElements = document.querySelectorAll('[data-reply-id]');
    replyElements.forEach(element => {
      observer.observe(element, {
        attributes: true,
        attributeFilter: ['class']
      });
    });
    
    return () => observer.disconnect();
  }, [replyList]); // replyList가 변경될 때마다 재실행
  
  // 답글 수정 핸들러들
  const handleReplyModifyStart = (replyId: string, replyElement: HTMLElement) => {
    setActiveReplyMenus(prev => ({ ...prev, [replyId]: false }));
    setReplyModifyStates(prev => ({ ...prev, [replyId]: true }));
    // is-modify 클래스 추가
    replyElement.classList.add('is-modify');
  };
  
  // 답글 삭제 핸들러들
  const handleReplyDeleteStart = (replyId: string) => {
    setActiveReplyMenus(prev => ({ ...prev, [replyId]: false }));
    setReplyDeleteConfirm(prev => ({ ...prev, [replyId]: true }));
  };
  
  const handleReplyDeleteConfirm = (replyId: string) => {
    if (onReplyDelete) {
      onReplyDelete(replyId, comment.id);
    }
    setReplyDeleteConfirm(prev => ({ ...prev, [replyId]: false }));
  };
  
  const handleReplyDeleteCancel = (replyId: string) => {
    setReplyDeleteConfirm(prev => ({ ...prev, [replyId]: false }));
  };
  
  
  const handleDelete = () => {
    setShowDeleteConfirm(true);
    cmtItemWrapRef.current?.classList.remove('is-active');
  };
  
  const handleDeleteConfirm = () => {
    if (onDelete) {
      try {
        onDelete(comment.id);
        setShowDeleteConfirm(false);
      } catch (error) {
        // 삭제 실패 처리
        console.error('댓글 삭제 실패:', error);
      }
    }
  };
  
  // 답글 데이터를 ShortsReplyData 형태로 변환
  const convertReplyData = (reply: CommentResDto): ShortsReplyData => {
    return {
      ...reply,
      id: reply.commentId,
      author: {
        name: reply.creatorNm,
        company: reply.creatorGroupNm,
        isAdmin: reply.creatorAdminYn === 'Y',
        profileImage: reply.creatorProfileUri
      },
      content: reply.contents,
      likes: reply.likeCnt,
      isLiked: reply.likeStatus === 'Y',
      isMine: reply.creatorId === myInfo.info?.userId, // 현재 사용자와 답글 작성자 비교
      isModified: !!reply.modifiedDt,
      createdDt: reply.createdDt,
      modifiedDt: reply.modifiedDt
    };
  };

  const getCommentClasses = () => {
    const classes = ['comment_item'];
    if (comment.type === 'fixed') classes.push('is-fix');
    if (comment.type === 'best') classes.push('is-best', 'is-appended');
    return classes.join(' ');
  };

  return (
    <div className={`shorts-media-comment ${getCommentClasses()} ${isReplyOpen ? 'is-appended' : ''}`}>
      {/* 일반 댓글 (삭제되지 않았거나 관리자가 삭제한 내 댓글) */}
      {(comment.delYn === 'N' || (comment.delUser === 'ADMIN' && comment.creatorId === myInfo.info?.userId)) && (
      <div className={classNames('comment_item_wrap', { 'is-mine': comment.creatorId === myInfo.info?.userId })} ref={cmtItemWrapRef}>
        <div className="comment_item_profile">
          <ProfileImage
            hasRetire={comment.creatorRetireYn === 'Y'}
            isAdmin={comment.creatorAdminYn === 'Y' || comment.adminRegYn === 'Y'}
            name={comment.creatorNm}
            score={comment.creatorActScore}
            image={comment.creatorProfileUri}
            isCommentList={true}
          />
        </div>
        <div className="comment_item_con">
          {/* 댓글 라벨 (고정/BEST) */}
          {comment.type === 'fixed' && <div className="comment_label">고정</div>}
          {comment.type === 'best' && (
            <div className="comment_label">BEST {comment.bestRank}</div>
          )}
          
          {/* 작성자 정보 */}
          <div className="comment_item_name">
            <a 
              href="#;" 
              className="comment_item_name_link popup-profile"
              onClick={(e) => {
                e.preventDefault();
                if (comment.creatorAdminYn === 'N' && comment.adminRegYn === 'N') {
                  setProfilePopupCommentCreatorId(comment.creatorId);
                  setShowProfilePopup(true);
                }
              }}
            >
              {comment.creatorAdminYn === 'Y' ? (
                '관리자'
              ) : (
                <div className="comment_creator_area">
                  {comment.creatorNm}
                  {comment.creatorGroupNm && comment.creatorRetireYn === 'N' && (
                    <span className="comment_item_company">
                      {comment.creatorGroupNm}
                    </span>
                  )}
                </div>
              )}
            </a>
            <span className="comment_item_time">
                {/* 고객 요청으로 인해, 관리자만 댓글 작성 날짜 및 시간 보이도록 변경 - 2025.02.26 */}
                {myInfo.info?.adminYn === 'Y' ? getCommentTime(comment.modifiedDt !== comment.createdDt) : ''}
                {comment.modifiedDt !== comment.createdDt ? ' (수정됨)' : ''}
            </span>
          </div>
          
          {/* 댓글 내용 */}
          <div 
            className="comment_item_desc"
            ref={(el) => {
              if (el && parsedComment) {
                handleMentionClick(el);
              }
            }}
            dangerouslySetInnerHTML={{
              __html: getDangerouslySetInnerHTMLValueBySanitizedHtml(parsedComment || comment.content),
            }}
          />
          
          {/* 댓글 수정 입력 폼 */}
          <div className="comment_modify">
            <div className="comment_input" style={{position: 'relative'}}>
              <style>
                {`
                  .shorts-media-comment .comment_modify .comment_cancel {
                    right: 40px !important;
                  }
                `}
              </style>
              <MentionModifyComment
                commentId={comment.id}
                contentsId={contentsId}
                commentPostType={CommentPostType.SHORTS_MEDIA}
                contents={comment.content}
                isReply={false}
                isFocusStart={true}
                cmtDivRef={modifyCmtDivRef}
                cmtInputRef={modifyCmtInputRef}
                cmtItemWrapRef={cmtItemWrapRef}
                tags={''}
                title={''}
                category={''}
                contentCreatorNm={''}
                createdDt={comment.createdDt}
                currentDt={moment().format('YYYY-MM-DD HH:mm:ss')}
                modify={(params: any) => {
                  if (onModify) {
                    onModify(comment.id, params.contents || params.content);
                  }
                }}
                commentSearchMailWidth={commentSearchMailWidth || (() => {})}
                commentIndex={0}
                adminRegYn={comment.adminRegYn}
                creatorId={comment.creatorId}
                creatorNm={comment.creatorNm}
                creatorEmail={comment.creatorEmail}
                creatorGroupNm={comment.creatorGroupNm || ''}
                creatorAdminYn={comment.creatorAdminYn === 'Y' ? 'Y' : 'N'}
                creatorActScore={0}
                creatorProfileUri={comment.creatorProfileUri}
                creatorRetireYn={comment.creatorRetireYn}
                delYn={comment.delYn}
                delUser={comment.delUser}
                fixedYn={comment.fixedYn}
                likeCnt={comment.likes}
                likeStatus={comment.isLiked ? 'Y' : 'N'}
                modifiedDt={comment.modifiedDt}
                replyCnt={comment.replyCount || 0}
                mobileRegYn={comment.mobileRegYn}
              />
            </div>
          </div>
          
          {/* 댓글 액션 (좋아요, 답글) */}
          <div className="comment_item_sub">
            <a 
              href="#;" 
              className={`comment_item_likes ${comment.isLiked ? 'is-active' : ''}`}
              onClick={(e) => {
                e.preventDefault();
                handleLike();
              }}
            >
              {comment.likes}
            </a>
            {comment.type !== 'fixed' && (
              <a 
                href="#;" 
                className="comment_item_reply"
                onClick={(e) => {
                  e.preventDefault();
                  handleReply();
                }}
              >
                답글
              </a>
            )}
          </div>
          
          {/* 답글 입력 폼 */}
          <div className={`comment_form is-pc ${isReplyInputOpen ? 'is-active' : ''}`}>
            <div className="comment_profile">
              <ProfileImage
                hasRetire={myInfo.info?.userStatus === 'RETIRE'}
                name={myInfo.info?.name || ''}
                score={myInfo.info?.actScore as number}
                image={myInfo.info?.profileImageUri}
                isAdmin={myInfo.info?.adminYn === 'Y'}
                isCommentList={true}
              />
            </div>
            <div className="comment_input" ref={replyInputRef}>
              <MentionAddComment
                contentsId={contentsId || ''}
                commentPostType={CommentPostType.SHORTS_MEDIA}
                tags={''}
                title={''}
                category={''}
                contentCreatorNm={''}
                isReply={true}
                commentId={comment.id}
                cmtDivRef={replyDivRef}
                cmtInputRef={replyInputRef}
                commentSearchMailWidth={commentSearchMailWidth}
                update={handleReplyUpdate}
                setReplyList={(replyList: CommentResDto[]) => {
                  // 답글 추가 후 부모 컴포넌트의 답글 목록 상태 업데이트
                  if (onSetReplyList) {
                    onSetReplyList(comment.id, replyList);
                  }
                  if (onUpdate) onUpdate();
                }}
              />
            </div>

            {/* 답글 입력 폼의 이모지 기능 */}
            {isReplyInputOpen && (
              <div className="comment_emoji is-pc">
                <ShortsMediaEmojiComment
                  clickEmoji={(emoji: string) => {
                    // 답글 입력창에 이모지 추가
                    const replyDiv = replyDivRef.current;
                    if (replyDiv) {
                      // 현재 내용 가져오기
                      const currentContent = replyDiv.innerHTML || '';
                      
                      // 이모지 추가
                      const newContent = currentContent + emoji;
                      replyDiv.innerHTML = newContent;
                      
                      // contentEditable div에 input 이벤트 디스패치하여 React가 변경을 감지하도록 함
                      const event = new Event('input', { bubbles: true });
                      replyDiv.dispatchEvent(event);
                      
                      // 커서를 텍스트 끝으로 이동
                      replyDiv.focus();
                      
                      // contentEditable에서 커서를 끝으로 이동하는 방법
                      const range = document.createRange();
                      const selection = window.getSelection();
                      if (selection) {
                        range.selectNodeContents(replyDiv);
                        range.collapse(false); // 끝으로 이동
                        selection.removeAllRanges();
                        selection.addRange(range);
                      }
                    }
                  }}
                  commentPostType={CommentPostType.SHORTS_MEDIA}
                  tags={''}
                  title={''}
                  category={''}
                  contentCreatorNm={''}
                  isReply={true}
                />
              </div>
            )}
          </div>
          
          {/* 답글 보기 버튼 */}
          {
            !!comment.replyCount && (
              <a
                href="#;"
                className="comment_reply_btn"
                onClick={async (e) => {
                  e.preventDefault();
                  await handleShowReplies();
                }}
              >
                답글<span className="comment_reply_num">{comment.replyCount || 0}</span>개
                <span className="comment_reply_more">{isReplyOpen ? '숨기기' : '보기'}</span>
              </a>
            )
          }

          {/* 수정/삭제 메뉴 - 내 댓글인 경우에만 표시 */}
          {comment.isMine && !isModifying && (
            <>
              <button 
                className="comment_dot" 
                onClick={() => cmtItemWrapRef.current?.classList.toggle('is-active')}
              >
                <img src="/assets/images/icon/ico-comment-dot.png" alt="comment menu" className="comment_dot_img" />
              </button>
              <div 
                className="comment_dot_menu" 
                style={{ 
                  zIndex: 80, 
                  right: '0', // 버튼 오른쪽 끝에 맞춤
                  transform: 'translateX(-10%)'
                }}
              >
                  <div className="comment_dot_list" style={{ zIndex: 80 }}>
                    <div className="comment_dot_item">
                      <a
                        className="comment_dot_modi"
                        href="#"
                        onClick={(e) => {
                          e.preventDefault();
                          handleModify();
                        }}
                      >
                        수정
                      </a>
                    </div>
                    <div className="comment_dot_item">
                      <a
                        className="comment_dot_del"
                        href="#"
                        onClick={(e) => {
                          e.preventDefault();
                          handleDelete();
                        }}
                      >
                        삭제
                      </a>
                    </div>
                  </div>
                </div>
            </>
          )}
        </div>
      </div>
      )}
      
      {/* 답글 목록 */}
      {isReplyOpen && (
        <div className="comment_reply">
          <div className="comment_reply_inner">
            <div className="comment_reply_wrap">
              {replyList && replyList.length > 0 ? replyList.map((reply) => {
                const replyData = convertReplyData(reply);
                const isReplyMenuActive = activeReplyMenus[replyData.id] || false;
                const isReplyModifying = replyModifyStates[replyData.id] || false;
                return (
                  <div key={reply.commentId} className="comment_item">
                    {/* 삭제되지 않았거나 관리자가 삭제한 내 답글 */}
                    {(reply.delYn === 'N' || (reply.delUser === 'ADMIN' && reply.creatorId === myInfo.info?.userId)) && (
                    <div 
                      className={`comment_item_wrap ${replyData.isMine ? 'is-mine' : ''} ${isReplyMenuActive ? 'is-active' : ''} ${isReplyModifying ? 'is-modify' : ''}`}
                      ref={(el) => {
                        if (el) {
                          // 답글 요소에 대한 참조를 저장하여 나중에 클래스 조작에 사용
                          el.setAttribute('data-reply-id', replyData.id);
                          // 답글별 ref 저장
                          (window as any)[`replyItemWrapRef_${replyData.id}`] = el;
                        }
                      }}
                    >
                      <div className="comment_item_profile">
                        <ProfileImage
                          hasRetire={replyData.creatorRetireYn === 'Y'}
                          isAdmin={replyData.creatorAdminYn === 'Y'}
                          name={replyData.creatorNm}
                          score={replyData?.creatorActScore as number}
                          image={replyData?.creatorProfileUri}
                          isCommentList={true}
                        />
                      </div>
                      <div className="comment_item_con">
                        <div className="comment_item_name">
                          <a 
                            href="#;" 
                            className="comment_item_name_link popup-profile"
                            onClick={(e) => {
                              e.preventDefault();
                              if (replyData.creatorAdminYn === 'N' && replyData.adminRegYn === 'N') {
                                setProfilePopupCommentCreatorId(replyData.creatorId);
                                setShowProfilePopup(true);
                              }
                            }}
                          >
                            {replyData.author.isAdmin ? (
                              '관리자'
                            ) : (
                              <>
                                {replyData.author.name}
                                {replyData.author.company && replyData.creatorRetireYn === 'N' && (
                                  <span className="comment_item_company">{replyData.author.company}</span>
                                )}
                              </>
                            )}
                          </a>
                          <span className="comment_item_time">
                              {/* 고객 요청으로 인해, 관리자만 댓글 작성 날짜 및 시간 보이도록 변경 - 2025.02.26 */}
                              {myInfo.info?.adminYn === 'Y' ? getCommentTime(reply.modifiedDt !== reply.createdDt) : ''}
                              {reply.modifiedDt !== reply.createdDt ? ' (수정됨)' : ''}
                          </span>
                        </div>
                        <div 
                          className="comment_item_desc"
                          ref={(el) => {
                            if (el && parsedReplies[replyData.id]) {
                              handleMentionClick(el);
                            }
                          }}
                          dangerouslySetInnerHTML={{
                            __html: getDangerouslySetInnerHTMLValueBySanitizedHtml(parsedReplies[replyData.id] || replyData.content),
                          }}
                        />
                        
                        {/* 답글 수정 입력창 */}
                        <div className="comment_modify">
                          <div className="comment_input" style={{position: 'relative'}}>
                            <style>
                              {`
                                .shorts-media-comment .comment_reply .comment_modify .comment_cancel {
                                  right: 40px !important;
                                }
                              `}
                            </style>
                            <MentionModifyComment
                              commentId={replyData.id}
                              contentsId={contentsId}
                              commentPostType={CommentPostType.SHORTS_MEDIA}
                              contents={replyData.content}
                              isReply={true}
                              isFocusStart={true}
                              refCommentId={comment.id}
                              cmtDivRef={React.createRef<HTMLDivElement>()}
                              cmtInputRef={React.createRef<HTMLDivElement>()}
                              cmtItemWrapRef={{
                                current: (window as any)[`replyItemWrapRef_${replyData.id}`] || null
                              } as React.RefObject<HTMLDivElement>}
                              tags={''}
                              title={''}
                              category={''}
                              contentCreatorNm={''}
                              createdDt={reply.createdDt}
                              currentDt={new Date().toISOString()}
                              modify={(params: any) => {
                                if (onReplyModify) {
                                  return onReplyModify(replyData.id, comment.id, params.contents || params.content);
                                }
                              }}
                              commentSearchMailWidth={commentSearchMailWidth || (() => {})}
                              commentIndex={0}
                              adminRegYn={'N'}
                              creatorId={replyData.author.name}
                              creatorNm={replyData.author.name}
                              creatorEmail={''}
                              creatorGroupNm={replyData.author.company || ''}
                              creatorAdminYn={replyData.author.isAdmin ? 'Y' : 'N'}
                              creatorActScore={0}
                              creatorProfileUri={replyData.author.profileImage}
                              creatorRetireYn={'N'}
                              delYn={'N'}
                              delUser={undefined}
                              fixedYn={'N'}
                              likeCnt={replyData.likes}
                              likeStatus={replyData.isLiked ? 'Y' : 'N'}
                              modifiedDt={reply.modifiedDt || reply.createdDt}
                              replyCnt={0}
                              mobileRegYn={'N'}
                              setReply={() => {
                                // 답글 수정 후 목록 새로고침
                                setTimeout(() => {
                                  // 답글 목록 즉시 새로고침
                                  import('../../../services').then(({ endpoints, fetcher }) => {
                                    fetcher.comment.getReplyComment(`${endpoints.comment.comment}/${comment.id}`).then(res => {
                                      if (res.resultCode === 200) {
                                        if (onSetReplyList) {
                                          onSetReplyList(comment.id, res.commentReplyList as CommentResDto[]);
                                        }
                                      }
                                    }).catch((error) => {
                                      console.error('답글 새로고침 실패:', error);
                                    });
                                  });
                                }, 100); // 수정 완료 후 약간의 지연
                                if (onUpdate) onUpdate();
                              }}
                            />
                          </div>
                        </div>
                        <div className="comment_item_sub">
                          <a 
                            href="#;" 
                            className={`comment_item_likes ${replyData.isLiked ? 'is-active' : ''}`}
                            onClick={(e) => {
                              e.preventDefault();
                              handleReplyLike(replyData.id);
                            }}
                          >
                            {replyData.likes}
                          </a>
                        </div>
                        
                        {/* 답글 수정/삭제 메뉴 - 기존 CommentItem과 동일한 구조 */}
                        {replyData.isMine && (
                          <>
                            <button 
                              className="comment_dot" 
                              onClick={() => {
                                setActiveReplyMenus(prev => ({
                                  ...prev,
                                  [replyData.id]: !prev[replyData.id]
                                }));
                              }}
                            >
                              <img src="/assets/images/icon/ico-comment-dot.png" alt="reply menu" className="comment_dot_img" />
                            </button>
                            <div 
                              className="comment_dot_menu" 
                              style={{ 
                                zIndex: 80,
                                right: '0',
                                transform: 'translateX(-20%)'
                              }}
                            >
                              <div className="comment_dot_list" style={{ zIndex: 80 }}>
                                <div className="comment_dot_item">
                                  <a 
                                    className="comment_dot_modi" 
                                    href="#" 
                                    onClick={(e) => {
                                      e.preventDefault();
                                      const replyElement = e.currentTarget.closest('.comment_item_wrap') as HTMLElement;
                                      if (replyElement) {
                                        handleReplyModifyStart(replyData.id, replyElement);
                                      }
                                    }}
                                  >
                                    수정
                                  </a>
                                </div>
                                <div className="comment_dot_item">
                                  <a 
                                    className="comment_dot_del" 
                                    href="#" 
                                    onClick={(e) => {
                                      e.preventDefault();
                                      handleReplyDeleteStart(replyData.id);
                                    }}
                                  >
                                    삭제
                                  </a>
                                </div>
                              </div>
                            </div>
                          </>
                        )}
                      </div>
                    </div>
                    )}
                    
                    {/* 삭제된 답글 표시 */}
                    {reply.delYn === 'Y' && (reply.delUser === 'SELF' || reply.creatorId !== myInfo.info?.userId) && (
                      <div className="comment_delete">{reply.contents}</div>
                    )}
                  </div>
                );
              }) : (
                <div className="comment_item">
                  <div className="comment_item_wrap">
                    <div className="comment_item_con">
                      <div className="comment_item_desc">답글이 없습니다.</div>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
      
      {/* 삭제된 댓글 표시 */}
      {comment.delYn === 'Y' && (comment.delUser === 'SELF' || comment.creatorId !== myInfo.info?.userId) && (
        <div className="comment_delete">{comment.content}</div>
      )}
      
      {/* 삭제 확인 팝업 */}
      {showDeleteConfirm && (
        <div className="confirm_popup comment_del_popup is-active">
          <div className="popup_dim" onClick={() => setShowDeleteConfirm(false)}></div>
          <div className="popup_inner">
            <p className="confirm_popup_text">해당 댓글을 삭제 하겠습니까?</p>
            <ul className="confirm_button_list">
              <li className="confirm_button_item">
                <button
                  type="button"
                  className="confirm_popup_button is-dim popup_button_cancel"
                  onClick={() => setShowDeleteConfirm(false)}
                >
                  취소
                </button>
              </li>
              <li className="confirm_button_item">
                <button
                  type="button"
                  className="confirm_popup_button popup_button_confirm"
                  onClick={handleDeleteConfirm}
                >
                  삭제
                </button>
              </li>
            </ul>
          </div>
        </div>
      )}
      
      {/* 답글 삭제 확인 팝업들 */}
      {Object.entries(replyDeleteConfirm).map(([replyId, isOpen]) => 
        isOpen && (
          <div key={`reply-delete-${replyId}`} className="confirm_popup comment_del_popup is-active">
            <div className="popup_dim" onClick={() => handleReplyDeleteCancel(replyId)}></div>
            <div className="popup_inner">
              <p className="confirm_popup_text">해당 답글을 삭제 하겠습니까?</p>
              <ul className="confirm_button_list">
                <li className="confirm_button_item">
                  <button
                    type="button"
                    className="confirm_popup_button is-dim popup_button_cancel"
                    onClick={() => handleReplyDeleteCancel(replyId)}
                  >
                    취소
                  </button>
                </li>
                <li className="confirm_button_item">
                  <button
                    type="button"
                    className="confirm_popup_button popup_button_confirm"
                    onClick={() => handleReplyDeleteConfirm(replyId)}
                  >
                    삭제
                  </button>
                </li>
              </ul>
            </div>
          </div>
        )
      )}
      
      {/* 멘션 프로필 팝업 */}
      {mentionProfilePopup && mentionUserId && (
        <ProfilePopup userId={mentionUserId} close={() => setMentionProfilePopup(false)} />
      )}

      {/* 일반 댓글 프로필 팝업 */}
      {showProfilePopup && profilePopupCommentCreatorId && <ProfilePopup userId={profilePopupCommentCreatorId as string} close={() => setShowProfilePopup(false)} />}
    </div>
  );
};

export default ShortsCommentItem; 